/**
 * OrderInvoice Component (BLU-328)
 *
 * Renders a single A4 packing slip invoice for print.
 * Matches the Printrooster packing slip format.
 *
 * CRITICAL: Uses ONLY inline styles.
 * printHtmlViaIframe copies innerHTML into an iframe with no Tailwind CSS.
 * Same constraint as GarmentLabel.tsx.
 */

import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import Barcode from 'react-barcode';
import { QRCodeSVG } from 'qrcode.react';
import { sanitizeBarcodeContent, isValidBarcodeContent } from '../../utils/barcode-config';
import { encodeGarmentQR, CURRENT_QR_VERSION } from '../../utils/qr-encoder';
import { formatOrderNumber, stripOrderNamePrefix } from '@threadheads/shared';

interface ShippingAddress {
  name: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  province: string | null;
  zip: string | null;
  country: string | null;
}

interface InvoiceGarmentUnit {
  id: string;
  sku: string;
  productionMethod: 'POD' | 'PAP';
}

interface InvoiceLineItem {
  sku: string;
  shopifySku: string | null;
  productName: string;
  variantTitle: string | null;
  quantity: number;
  imageUrl: string | null;
  customPreviewUrl: string | null;
  garments: InvoiceGarmentUnit[];
  /** BLU-861: false for gift-card / requires_shipping=false lines — these never get a
   * garment unit, so absence of one must not trigger the pending-order-change notice.
   * Defaults to true (older cached payloads without this field behave as today). */
  isShippable?: boolean;
}

export interface OrderInvoiceProps {
  order: {
    orderId: string;
    orderName: string;
    orderDate: string;
    customerName: string | null;
    shippingAddress: ShippingAddress | null;
    tags: string[] | null;
    shippingMethod: string | null;
    note: string | null;
    lineItems: InvoiceLineItem[];
  };
  locationName: string;
  batchNumber: number;
}

// Inline styles object — no Tailwind available in print iframe
const styles = {
  invoice: {
    fontFamily: 'Arial, Helvetica, "PingFang SC", "Microsoft YaHei", sans-serif',
    color: '#111',
    fontSize: '11pt',
    lineHeight: '1.4',
    maxWidth: '100%',
  } as React.CSSProperties,
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingBottom: '12px',
    borderBottom: '2px solid #111',
    marginBottom: '16px',
  } as React.CSSProperties,
  orderNumberLarge: {
    fontSize: '18pt',
    fontWeight: 'bold',
    textAlign: 'right',
    marginTop: '4px',
  } as React.CSSProperties,
  metaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '12px',
    gap: '24px',
  } as React.CSSProperties,
  metaBlock: {
    flex: 1,
  } as React.CSSProperties,
  metaLabel: {
    fontSize: '8pt',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#666',
    marginBottom: '4px',
  } as React.CSSProperties,
  metaValue: {
    fontSize: '10pt',
    marginBottom: '2px',
  } as React.CSSProperties,
  tagsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    marginBottom: '12px',
  } as React.CSSProperties,
  tag: {
    border: '1px solid #333',
    padding: '2px 8px',
    fontSize: '8pt',
    fontWeight: 'bold',
    borderRadius: '2px',
    display: 'inline-block',
  } as React.CSSProperties,
  locationBanner: {
    background: '#111',
    color: '#fff',
    padding: '6px 10px',
    fontSize: '9pt',
    fontWeight: 'bold',
    marginBottom: '0',
  } as React.CSSProperties,
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '0',
  } as React.CSSProperties,
  th: {
    background: '#f3f4f6',
    fontSize: '8pt',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    padding: '6px 8px',
    border: '1px solid #ddd',
    textAlign: 'left',
  } as React.CSSProperties,
  td: {
    padding: '8px',
    border: '1px solid #ddd',
    verticalAlign: 'middle',
  } as React.CSSProperties,
  qtyCell: {
    textAlign: 'center',
    fontSize: '22pt',
    fontWeight: 'bold',
    width: '50px',
    padding: '8px',
    border: '1px solid #ddd',
    verticalAlign: 'middle',
    fontVariantNumeric: 'tabular-nums',
  } as React.CSSProperties,
  imgCell: {
    width: '160px',
    padding: '4px',
    border: '1px solid #ddd',
    verticalAlign: 'middle',
    textAlign: 'center',
  } as React.CSSProperties,
  productImg: {
    width: '150px',
    height: '150px',
    objectFit: 'contain',
    display: 'inline-block',
    verticalAlign: 'middle',
  } as React.CSSProperties,
  // Custom artwork (the customer's design) — framed on an off-white plate so
  // transparent / white-ink designs stay visible on white paper.
  customArtworkImg: {
    width: '150px',
    height: '150px',
    objectFit: 'contain',
    display: 'inline-block',
    verticalAlign: 'middle',
    border: '1px solid #e5e7eb',
    background: '#f9fafb',
    borderRadius: '2px',
  } as React.CSSProperties,
  imgPlaceholder: {
    width: '150px',
    height: '150px',
    background: '#f3f4f6',
    border: '1px dashed #d1d5db',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '9pt',
    color: '#6b7280',
    textAlign: 'center',
  } as React.CSSProperties,
  productName: {
    fontWeight: 'bold',
    fontSize: '10pt',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  } as React.CSSProperties,
  variantTitle: {
    fontSize: '9pt',
    color: '#555',
    marginTop: '2px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  } as React.CSSProperties,
  barcodeCell: {
    width: '140px',
    textAlign: 'center',
    padding: '4px',
    border: '1px solid #ddd',
    verticalAlign: 'middle',
  } as React.CSSProperties,
  footer: {
    marginTop: '16px',
    borderTop: '1px solid #ddd',
    paddingTop: '8px',
    fontSize: '8pt',
    color: '#888',
    textAlign: 'center',
  } as React.CSSProperties,
  noAddress: {
    fontStyle: 'italic',
    color: '#aaa',
    fontSize: '9pt',
  } as React.CSSProperties,
  // In-cell marker for a garment-less shippable line (see the full-width
  // pendingNoticeBand below for the actual explanation).
  noQrMarker: {
    fontWeight: 'bold',
    fontSize: '9pt',
    color: '#b45309',
    lineHeight: '1.3',
  } as React.CSSProperties,
  // In-cell marker for a garment-less line that is NOT shippable (gift card /
  // requires_shipping=false) — expected to have no QR, not a pending notice.
  nonShippableDash: {
    color: '#999',
    fontSize: '14pt',
  } as React.CSSProperties,
  // Full-width band, same visual weight as the order-note callout (~lines 393-407).
  pendingNoticeBand: {
    padding: '8px 10px',
    background: '#fffbeb',
    border: '1px solid #f59e0b',
    borderRadius: '4px',
    fontSize: '9pt',
    breakInside: 'avoid',
    pageBreakInside: 'avoid',
  } as React.CSSProperties,
  pendingNoticeBandTitle: {
    fontSize: '10pt',
    fontWeight: 'bold',
    color: '#92400e',
  } as React.CSSProperties,
  pendingNoticeBandBody: {
    fontSize: '8pt',
    color: '#78350f',
    marginTop: '2px',
    lineHeight: '1.35',
    textAlign: 'left',
  } as React.CSSProperties,
} as const;

// Bilingual by design: this is a printed floor document for Chinese operators —
// both languages must render regardless of the viewer's UI locale, and zh
// locale files load lazily, so a fixed-lng t() lookup cannot be used here.
const PENDING_ORDER_CHANGE_NOTICE = {
  zhTitle: '待处理订单变更：该商品并未取消',
  zhBody: '请在“问题 → 订单变更”中修正或按现状接受，然后重新打印本发票。',
  enTitle: 'PENDING ORDER CHANGE: item is NOT cancelled',
  enBody: 'Fix it on Issues → Order Changes, then reprint this invoice.',
} as const;

/**
 * Validates that an image URL is from a trusted CDN domain.
 */
function isShopifyImageUrl(url: string | null): boolean {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return (
      parsed.hostname.endsWith('.shopify.com') ||
      parsed.hostname.endsWith('.shopifycdn.com') ||
      parsed.hostname.endsWith('.supabase.co')
    );
  } catch {
    return false;
  }
}

/**
 * Request a print-appropriate thumbnail from Shopify's CDN instead of the full-res
 * master. Shopify honours a `width` query param; ~300px is 2× the 150px print box for
 * crisp output and a faster print dialog (printing waits on every image's load event).
 * Non-Shopify hosts (e.g. Supabase) are returned unchanged.
 */
function sizedImageUrl(url: string, width = 300): string {
  try {
    const parsed = new URL(url);
    if (parsed.hostname.endsWith('.shopify.com') || parsed.hostname.endsWith('.shopifycdn.com')) {
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    }
    return url;
  } catch {
    return url;
  }
}

function formatDate(isoDate: string): string {
  try {
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return isoDate;
  }
}

export function OrderInvoice({ order, locationName, batchNumber }: OrderInvoiceProps) {
  const { t } = useTranslation('production');
  const barcodeSku = stripOrderNamePrefix(order.orderName);

  return (
    <div className="invoice" style={styles.invoice}>
      {/* Header: Order barcode + order number */}
      <div style={styles.header}>
        <div>
          <div style={{ fontSize: '14pt', fontWeight: 'bold' }}>
            {t('invoice.document.title')}
          </div>
          {batchNumber > 0 && (
            <div style={{ fontSize: '9pt', color: '#666', marginTop: '2px' }}>
              {t('invoice.document.batch', { number: batchNumber })}
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          {isValidBarcodeContent(barcodeSku) && (
            <Barcode
              value={sanitizeBarcodeContent(barcodeSku)}
              format="CODE128"
              width={2}
              height={50}
              fontSize={10}
              margin={0}
              displayValue={true}
              background="#ffffff"
              lineColor="#000000"
            />
          )}
        </div>
      </div>

      {/* Receipt / Shipping meta blocks */}
      <div style={styles.metaRow}>
        <div style={styles.metaBlock}>
          <div style={styles.metaLabel}>{t('invoice.document.orderDetails')}</div>
          <div style={{ ...styles.metaValue, fontWeight: 'bold', fontSize: '14pt' }}>
            {formatOrderNumber(order.orderName)}
          </div>
          <div style={styles.metaValue}>{t('invoice.document.date', { date: formatDate(order.orderDate) })}</div>
          {order.shippingMethod && (
            <div style={styles.metaValue}>{t('invoice.document.method', { method: order.shippingMethod })}</div>
          )}
        </div>
        <div style={styles.metaBlock}>
          <div style={styles.metaLabel}>{t('invoice.document.shipTo')}</div>
          {order.shippingAddress ? (
            <>
              {(order.shippingAddress.name || order.customerName) && (
                <div style={{ ...styles.metaValue, fontWeight: 'bold' }}>
                  {order.shippingAddress.name || order.customerName}
                </div>
              )}
              {order.shippingAddress.address1 && (
                <div style={styles.metaValue}>{order.shippingAddress.address1}</div>
              )}
              {order.shippingAddress.address2 && (
                <div style={styles.metaValue}>{order.shippingAddress.address2}</div>
              )}
              <div style={styles.metaValue}>
                {[
                  order.shippingAddress.city,
                  order.shippingAddress.province,
                  order.shippingAddress.zip,
                ]
                  .filter(Boolean)
                  .join(' ')}
              </div>
              {order.shippingAddress.country && (
                <div style={styles.metaValue}>{order.shippingAddress.country}</div>
              )}
            </>
          ) : (
            <div style={styles.noAddress}>{t('invoice.document.noAddress')}</div>
          )}
        </div>
      </div>

      {/* Tags */}
      {order.tags && order.tags.length > 0 && (
        <div style={styles.tagsRow}>
          {order.tags.map((tag, i) => (
            <span key={i} style={styles.tag}>
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Order notes */}
      {order.note && (
        <div style={{
          padding: '8px 10px',
          marginBottom: '8px',
          background: '#fffbeb',
          border: '1px solid #f59e0b',
          borderRadius: '4px',
          fontSize: '9pt',
        }}>
          <div style={{ fontWeight: 'bold', fontSize: '8pt', textTransform: 'uppercase', color: '#92400e', marginBottom: '2px' }}>
            {t('invoice.document.orderNote')}
          </div>
          <div style={{ color: '#78350f' }}>{order.note}</div>
        </div>
      )}

      {/* Location banner */}
      <div style={styles.locationBanner}>{locationName}</div>

      {/* Line items table */}
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={{ ...styles.th, width: '50px', textAlign: 'center' }}>{t('invoice.document.qty')}</th>
            <th style={{ ...styles.th, width: '160px' }}>{t('invoice.document.image')}</th>
            <th style={styles.th}>{t('invoice.document.product')}</th>
            <th style={{ ...styles.th, width: '140px', textAlign: 'center' }}>{t('invoice.document.garmentLabel')}</th>
          </tr>
        </thead>
        <tbody>
          {order.lineItems.length === 0 && (
            <tr>
              <td colSpan={4} style={{ ...styles.td, textAlign: 'center', color: '#999', fontStyle: 'italic' }}>
                {t('invoice.document.noItems')}
              </td>
            </tr>
          )}
          {order.lineItems.map((item, index) => {
            const barcodeValue = item.shopifySku || item.sku;
            const safeImageUrl =
              item.imageUrl && isShopifyImageUrl(item.imageUrl) ? sizedImageUrl(item.imageUrl) : null;
            // Prefer the custom artwork preview (the design printers see) — a signed
            // *.supabase.co URL, used as-is (no Shopify resize). Fall back to the generic
            // product image when the preview is absent or fails to load on screen.
            const safePreviewUrl =
              item.customPreviewUrl && isShopifyImageUrl(item.customPreviewUrl)
                ? item.customPreviewUrl
                : null;
            const primaryImageUrl = safePreviewUrl ?? safeImageUrl;
            const isCustomArtwork = safePreviewUrl !== null;
            // On-screen only (the print iframe doesn't fire onError): if the preview
            // fails, swap to the generic image once, then reveal the placeholder.
            const imageFallbackUrl = safePreviewUrl && safeImageUrl ? safeImageUrl : null;
            // BLU-861: a garment-less line is only worth flagging when it was
            // supposed to ship — gift-card / requires_shipping=false lines never
            // get a garment unit by design. Default-true so older cached
            // payloads (pre-isShippable) keep today's behaviour.
            const showPendingNotice = item.garments.length === 0 && item.isShippable !== false;

            return (
              <Fragment key={index}>
                <tr>
                  <td style={styles.qtyCell}>{item.quantity}</td>
                  <td style={styles.imgCell}>
                    {primaryImageUrl ? (
                      <img
                        src={primaryImageUrl}
                        alt={isCustomArtwork ? `Print preview: ${item.productName}` : item.productName}
                        style={isCustomArtwork ? styles.customArtworkImg : styles.productImg}
                        data-fallback={imageFallbackUrl ?? undefined}
                        onError={(e) => {
                          const img = e.currentTarget as HTMLImageElement;
                          const fallback = img.getAttribute('data-fallback');
                          if (fallback && img.src !== fallback) {
                            img.removeAttribute('data-fallback');
                            img.src = fallback;
                            return;
                          }
                          img.style.display = 'none';
                          const sibling = img.nextElementSibling;
                          if (sibling) (sibling as HTMLElement).style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      style={{
                        ...styles.imgPlaceholder,
                        display: primaryImageUrl ? 'none' : 'flex',
                      }}
                    >
                      {t('invoice.document.noImg')}
                    </div>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.productName}>{item.productName}</div>
                    {item.variantTitle && (
                      <div style={styles.variantTitle}>{item.variantTitle}</div>
                    )}
                  </td>
                  <td style={styles.barcodeCell}>
                    {item.garments.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center' }}>
                        {item.garments.map((garment) => {
                          const qrValue = encodeGarmentQR({
                            // Phase 50 / BLU-233: V2 payload. m:'DTG' until
                            // print_technology threads through (50.01.3).
                            v: CURRENT_QR_VERSION,
                            g: garment.id,
                            o: order.orderName,
                            s: garment.sku,
                            m: 'DTG',
                            t: garment.productionMethod === 'PAP' ? 'P&P' : 'POD',
                          });
                          return (
                            <QRCodeSVG
                              key={garment.id}
                              value={qrValue}
                              size={120}
                              level="L"
                              marginSize={4}
                            />
                          );
                        })}
                      </div>
                    ) : showPendingNotice ? (
                      <div style={styles.noQrMarker}>
                        无二维码
                        <br />
                        NO QR
                      </div>
                    ) : (
                      <div style={styles.nonShippableDash}>-</div>
                    )}
                    <div style={{ fontSize: '7pt', color: '#666', marginTop: '2px', overflowWrap: 'anywhere', fontVariantNumeric: 'tabular-nums' }}>
                      {barcodeValue}
                    </div>
                  </td>
                </tr>
                {showPendingNotice && (
                  <tr>
                    <td colSpan={4} style={{ padding: '4px 0', border: 'none' }}>
                      <div style={styles.pendingNoticeBand}>
                        <div style={styles.pendingNoticeBandTitle}>{PENDING_ORDER_CHANGE_NOTICE.zhTitle}</div>
                        <div style={styles.pendingNoticeBandBody}>{PENDING_ORDER_CHANGE_NOTICE.zhBody}</div>
                        <div style={{ ...styles.pendingNoticeBandTitle, marginTop: '6px' }}>{PENDING_ORDER_CHANGE_NOTICE.enTitle}</div>
                        <div style={styles.pendingNoticeBandBody}>{PENDING_ORDER_CHANGE_NOTICE.enBody}</div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>

      {/* Footer */}
      <div style={styles.footer}>
        {t('invoice.document.totalItems', { count: order.lineItems.reduce((sum, item) => sum + item.quantity, 0) })}
      </div>
    </div>
  );
}
